import * as _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { RuleGroupType } from 'react-querybuilder';

export type QueryFilter = {
  id: string;
  filterName: string;
  query: RuleGroupType;
  isActive: boolean;
};

export type LocalStorageQueryFilterState = {
  [creditFacilityId: string]: QueryFilter[];
};

export const queryBuilderDefaultQuery: RuleGroupType = {
  combinator: 'and',
  rules: [],
};

/**
 * @jendevelops [receivables-dashboard TODO]: create universal local storage to prevent key collisions
 */
export const COLUMN_FILTER_STATE_KEY = 'receivablesTableQueryFilters';

/**
 * Creates storage event to be dispatched on saving query filters to local storage
 */
const createStorageEvent = ({
  oldValue,
  newValue,
}: {
  oldValue: string;
  newValue: string;
}): StorageEvent =>
  new StorageEvent('storage', {
    key: COLUMN_FILTER_STATE_KEY,
    oldValue,
    newValue,
  });

/**
 * Sets local storage item and dispatches storage event when query filters is saved to local storage
 *
 * Note: this will cause multiple events to fire for other windows on the same domain
 */
const setLocalStorageItem = ({
  oldValue,
  newValue,
}: {
  oldValue: string;
  newValue: string;
}): void => {
  localStorage.setItem(COLUMN_FILTER_STATE_KEY, newValue);
  window.dispatchEvent(createStorageEvent({ oldValue, newValue }));
};

/**
 * Initializes local storage query filters for an organization + credit facility if they don't exist
 */
export const initializeReceivableResponseTapeQueryFilterLocalStorage = ({
  organizationId,
  creditFacilityId,
}: {
  organizationId: string;
  creditFacilityId: string;
}): LocalStorageQueryFilterState => {
  const filtersJSON = localStorage.getItem(COLUMN_FILTER_STATE_KEY);
  const filters: LocalStorageQueryFilterState = JSON.parse(filtersJSON || '{}');

  const organizationCreditFacilityKey = `${organizationId}+${creditFacilityId}`;

  const newFilters: LocalStorageQueryFilterState = { ...filters };
  if (!_.isArray(newFilters?.[organizationCreditFacilityKey])) {
    newFilters[organizationCreditFacilityKey] = [];
  }
  const newFiltersJSON = JSON.stringify(newFilters);
  if (filtersJSON !== newFiltersJSON) {
    setLocalStorageItem({
      oldValue: filtersJSON || '{}',
      newValue: newFiltersJSON,
    });
  }
  return newFilters;
};

/**
 * adds filter to local storage filters list, or updates existing filter in list
 * initializes filters list if it doesn't exist
 */
const upsertReceivableResponseTapeQueryFilterToLocalStorage = ({
  queryFilter,
  organizationId,
  creditFacilityId,
}: {
  queryFilter: QueryFilter;
  organizationId: string;
  creditFacilityId: string;
}): QueryFilter[] => {
  const filters: LocalStorageQueryFilterState = initializeReceivableResponseTapeQueryFilterLocalStorage(
    { organizationId, creditFacilityId },
  );
  const queryFiltersKey = `${organizationId}+${creditFacilityId}`;

  const newFilters = { ...filters };
  const newFiltersCFFilters = [...newFilters[queryFiltersKey]];

  const filterIndex = newFiltersCFFilters.findIndex((f: QueryFilter) => {
    return f.id === queryFilter.id;
  });

  if (filterIndex > -1) {
    newFiltersCFFilters[filterIndex] = queryFilter;
  } else {
    newFiltersCFFilters.unshift(queryFilter);
  }
  newFilters[queryFiltersKey] = newFiltersCFFilters;
  setLocalStorageItem({
    oldValue: JSON.stringify(filters),
    newValue: JSON.stringify(newFilters),
  });
  return filters[queryFiltersKey];
};

/**
 * deletes filter from local storage credit facility filters list
 */
const deleteReceivableResponseTapeQueryFilterFromLocalStorage = ({
  queryFilter,
  organizationId,
  creditFacilityId,
}: {
  queryFilter: QueryFilter;
  organizationId: string;
  creditFacilityId: string;
}): QueryFilter[] => {
  const filters: LocalStorageQueryFilterState = initializeReceivableResponseTapeQueryFilterLocalStorage(
    { organizationId, creditFacilityId },
  );
  const newFilters: LocalStorageQueryFilterState = { ...filters };

  const creditFacilityFilters =
    newFilters[`${organizationId}+${creditFacilityId}`];

  const filterIndex = creditFacilityFilters.findIndex(
    (f: QueryFilter) => f.id === queryFilter.id,
  );

  if (filterIndex > -1) {
    creditFacilityFilters.splice(filterIndex, 1);
  }
  setLocalStorageItem({
    oldValue: JSON.stringify(filters),
    newValue: JSON.stringify(newFilters),
  });

  return creditFacilityFilters;
};

const initStorageEventListener = ({
  organizationId,
  creditFacilityId,
  setQueryFilters,
}: {
  organizationId: string;
  creditFacilityId: string | null;
  setQueryFilters: React.Dispatch<React.SetStateAction<QueryFilter[]>>;
}) => {
  return (event: StorageEvent) => {
    // only update query filters if storage update event is in response to a query filter update
    if (event.key !== COLUMN_FILTER_STATE_KEY || !creditFacilityId) return;

    const filtersJSON = localStorage.getItem(COLUMN_FILTER_STATE_KEY);
    const filters: LocalStorageQueryFilterState = JSON.parse(
      filtersJSON || '{}',
    );
    setQueryFilters(filters[`${organizationId}+${creditFacilityId}`]);
  };
};

/**
 * overwrites credit facility local storage query filters list with provided list
 */
const overwriteReceivableResponseTapeQueryFiltersInLocalStorage = ({
  queryFilters,
  organizationId,
  creditFacilityId,
}: {
  organizationId: string;
  creditFacilityId: string;
  queryFilters: QueryFilter[];
}): QueryFilter[] => {
  const filters: LocalStorageQueryFilterState = initializeReceivableResponseTapeQueryFilterLocalStorage(
    { organizationId, creditFacilityId },
  );
  const queryFiltersKey = `${organizationId}+${creditFacilityId}`;
  const newFilters: LocalStorageQueryFilterState = { ...filters };

  newFilters[queryFiltersKey] = queryFilters;

  setLocalStorageItem({
    oldValue: JSON.stringify(filters),
    newValue: JSON.stringify(newFilters),
  });
  return newFilters[queryFiltersKey];
};

/**
 * deactivates all filters in local storage for credit facility
 */

export const deactivateAllReceivableResponseTapeQueryFiltersInLocalStorage = ({
  organizationId,
  creditFacilityId,
}: {
  organizationId: string;
  creditFacilityId: string;
}) => {
  const queryFilters = initializeReceivableResponseTapeQueryFilterLocalStorage({
    organizationId,
    creditFacilityId,
  })[`${organizationId}+${creditFacilityId}`];

  const deactivatedQueryFilters = queryFilters.map((queryFilter) => ({
    ...queryFilter,
    isActive: false,
  }));

  overwriteReceivableResponseTapeQueryFiltersInLocalStorage({
    queryFilters: deactivatedQueryFilters,
    organizationId,
    creditFacilityId,
  });
};

export const useCreditFacilityQueryFilters = ({
  organizationId,
  creditFacilityId,
}: {
  organizationId: string;
  creditFacilityId: string | null;
}) => {
  const queryFiltersKey = `${organizationId}+${creditFacilityId}`;

  const [queryFilters, setQueryFilters] = useState<QueryFilter[]>(
    creditFacilityId
      ? initializeReceivableResponseTapeQueryFilterLocalStorage({
          organizationId,
          creditFacilityId,
        })[queryFiltersKey]
      : [],
  );

  // updates query filters when credit facility id changes
  useEffect(() => {
    if (!creditFacilityId) {
      setQueryFilters([]);
    } else {
      setQueryFilters(
        initializeReceivableResponseTapeQueryFilterLocalStorage({
          organizationId,
          creditFacilityId,
        })[queryFiltersKey],
      );
    }
  }, [organizationId, creditFacilityId, queryFiltersKey]);

  // listens to local storage updates to update query filters state
  // @jendevelops [receivables-dashboard TODO]: remove listener and opt for coupled state update when "unmounted component" error resolved
  useEffect(() => {
    const storageEventListener = initStorageEventListener({
      organizationId,
      creditFacilityId,
      setQueryFilters,
    });
    window.addEventListener('storage', storageEventListener);
    return () => {
      window.removeEventListener('storage', storageEventListener);
    };
  }, [creditFacilityId, organizationId]);

  /**
   * Flips `isActivated` property of query filter in local state
   *
   * Does not trigger athena fetch update because only updates state, not local storage
   */
  const toggleIsActivateLocalState = useCallback(
    ({ queryFilter }: { queryFilter: QueryFilter }): void => {
      setQueryFilters((previousQueryFilters: QueryFilter[]) => {
        const newQueryFilters: QueryFilter[] = [...previousQueryFilters];
        const queryFilterIndex = newQueryFilters.findIndex(
          (f) => f.id === queryFilter.id,
        );
        if (queryFilterIndex > -1) {
          newQueryFilters[queryFilterIndex] = {
            ...newQueryFilters[queryFilterIndex],
            isActive: !newQueryFilters[queryFilterIndex].isActive,
          };
        }
        return newQueryFilters;
      });
    },
    [],
  );

  /**
   * Deactivates all filters in local state
   *
   * Does not trigger athena fetch update because only updates state, not local storage
   */
  const deactivateAllLocalStateQueryFilters = useCallback((): void => {
    setQueryFilters((previousQueryFilters: QueryFilter[]) => {
      return previousQueryFilters.map((queryFilter) => ({
        ...queryFilter,
        isActive: false,
      }));
    });
  }, []);

  /**
   * Overwrites local storage query filters for credit facility with local state filters
   */
  const saveLocalStateFiltersToLocalStorage = useCallback(() => {
    if (creditFacilityId) {
      overwriteReceivableResponseTapeQueryFiltersInLocalStorage({
        organizationId,
        creditFacilityId,
        queryFilters,
      });
    }
  }, [creditFacilityId, organizationId, queryFilters]);

  /**
   * Adds or updates query filter in local storage
   */
  const upsertQueryFilter = useCallback(
    ({ queryFilter }: { queryFilter: QueryFilter }): QueryFilter[] => {
      // @jendevelops TODO add warn when trying to upsert on no cf
      if (!creditFacilityId) return [];
      return upsertReceivableResponseTapeQueryFilterToLocalStorage({
        organizationId,
        creditFacilityId,
        queryFilter,
      });
    },
    [creditFacilityId, organizationId],
  );

  /**
   * Deletes query filter from local storage
   */
  const deleteQueryFilter = useCallback(
    ({ queryFilter }: { queryFilter: QueryFilter }): QueryFilter[] => {
      // @jendevelops TODO add warn when trying to delete on no cf
      if (!creditFacilityId) return [];
      return deleteReceivableResponseTapeQueryFilterFromLocalStorage({
        organizationId,
        creditFacilityId,
        queryFilter,
      });
    },
    [creditFacilityId, organizationId],
  );

  return {
    queryFilters,
    deactivateAllLocalStateQueryFilters,
    saveLocalStateFiltersToLocalStorage,
    toggleIsActivateLocalState,
    upsertQueryFilter,
    deleteQueryFilter,
  };
};
